import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardCheck, Loader2, Mic, MicOff, Save, FileCheck2, RotateCcw } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/installer-upload`;

const Field = ({ label, children, hint }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10.5px] font-bold uppercase tracking-wider text-zinc-400">{label}</span>
    {children}
    {hint && <span className="text-[12px] text-zinc-300 mt-0.5">{hint}</span>}
  </label>
);

const inputCls = "h-11 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 text-zinc-50 focus:border-[#00E575] outline-none text-sm";

const Toggle3 = ({ value, onChange, testId }) => {
  const opts = [
    { v: true, label: "Ja", color: "bg-emerald-500 text-emerald-50" },
    { v: false, label: "Nein", color: "bg-rose-500 text-rose-50" },
    { v: null, label: "—", color: "bg-zinc-600 text-zinc-200" },
  ];
  return (
    <div className="inline-flex rounded-xl overflow-hidden border border-zinc-700" data-testid={testId}>
      {opts.map((o) => {
        const selected = value === o.v;
        return (
          <button
            type="button"
            key={String(o.v)}
            onClick={() => onChange(o.v)}
            className={`px-3 h-10 text-xs font-bold transition ${selected ? o.color : "bg-[#09090B] text-zinc-400 hover:bg-zinc-800"}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

const StatusToggle = ({ value, onChange, testId }) => {
  const opts = [
    { v: "ok", label: "OK", color: "bg-emerald-500 text-emerald-50" },
    { v: "error", label: "Fehler", color: "bg-rose-500 text-rose-50" },
    { v: null, label: "—", color: "bg-zinc-600 text-zinc-200" },
  ];
  return (
    <div className="inline-flex rounded-xl overflow-hidden border border-zinc-700" data-testid={testId}>
      {opts.map((o) => {
        const selected = value === o.v;
        return (
          <button
            type="button"
            key={String(o.v)}
            onClick={() => onChange(o.v)}
            className={`px-3 h-10 text-xs font-bold transition ${selected ? o.color : "bg-[#09090B] text-zinc-400 hover:bg-zinc-800"}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

// Small Whisper dictation button — re-uses existing public voice endpoint.
const NotesDictation = ({ token, onTranscript, disabled }) => {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recorderRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        if (blob.size < 1500) {
          toast.error("Aufnahme zu kurz");
          return;
        }
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const r = await axios.post(`${API_BASE}/${token}/voice`, fd);
          if (r.data?.text) onTranscript(r.data.text);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Transkription fehlgeschlagen");
        } finally {
          setBusy(false);
        }
      };
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Mikrofon-Zugriff verweigert");
    }
  };

  const stop = () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    setRecording(false);
  };

  return (
    <button
      type="button"
      disabled={disabled || busy}
      data-testid="commissioning-notes-dictate"
      onClick={recording ? stop : start}
      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold transition ${recording ? "bg-rose-500 text-white animate-pulse" : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"} disabled:opacity-50`}
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : recording ? <MicOff size={14} /> : <Mic size={14} />}
      {recording ? "Stop" : "Diktieren"}
    </button>
  );
};

// Touch signature pad
const SignaturePad = ({ value, onChange, label, testId }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });

  const ctx = () => canvasRef.current?.getContext("2d");

  const initCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * dpr;
    c.height = c.offsetHeight * dpr;
    const x = ctx();
    if (x) {
      x.scale(dpr, dpr);
      x.lineCap = "round";
      x.lineWidth = 2;
      x.strokeStyle = "#fff";
    }
  };

  useEffect(() => {
    initCanvas();
    if (value) {
      const img = new Image();
      img.onload = () => {
        const x = ctx();
        if (!x || !canvasRef.current) return;
        x.drawImage(img, 0, 0, canvasRef.current.offsetWidth, canvasRef.current.offsetHeight);
      };
      img.src = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pos = (evt) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = evt.touches?.[0] || evt;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pos(e);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const x = ctx();
    const p = pos(e);
    x.beginPath();
    x.moveTo(lastRef.current.x, lastRef.current.y);
    x.lineTo(p.x, p.y);
    x.stroke();
    lastRef.current = p;
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange(dataUrl);
  };

  const clear = () => {
    const x = ctx();
    if (x && canvasRef.current) {
      x.clearRect(0, 0, canvasRef.current.offsetWidth, canvasRef.current.offsetHeight);
    }
    onChange("");
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] font-bold uppercase tracking-wider text-zinc-400">{label}</span>
        <button type="button" onClick={clear} className="text-[12px] text-zinc-300 hover:text-zinc-200 inline-flex items-center gap-1">
          <RotateCcw size={11} /> Löschen
        </button>
      </div>
      <canvas
        ref={canvasRef}
        data-testid={testId}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="block w-full h-32 bg-[#09090B] border border-zinc-700 rounded-xl cursor-crosshair touch-none"
      />
    </div>
  );
};

export const CommissioningProtocol = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [data, setData] = useState(null);
  const [done, setDone] = useState(false);

  const [installerSig, setInstallerSig] = useState({ name: "", data_url: "" });
  const [customerSig, setCustomerSig] = useState({ name: "", data_url: "" });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/${token}/commissioning`);
        setData(r.data);
        if (r.data?.protocol?.finalized_at) setDone(true);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Protokoll konnte nicht geladen werden");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const update = (section, key, value) =>
    setData((d) => ({ ...d, protocol: { ...d.protocol, [section]: { ...d.protocol[section], [key]: value } } }));

  const sectionPayload = () => ({
    device: data.protocol.device, hydraulics: data.protocol.hydraulics,
    electrical: data.protocol.electrical, function: data.protocol.function,
    measured: data.protocol.measured, handover: data.protocol.handover,
  });

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/${token}/commissioning`, sectionPayload());
      toast.success("Zwischenstand gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const finalize = async () => {
    if (!installerSig.name.trim() || !installerSig.data_url) {
      toast.error("Bitte unterschreiben Sie als Installation und tragen Ihren Namen ein.");
      return;
    }
    if (!customerSig.name.trim() || !customerSig.data_url) {
      toast.error("Bitte Kundenunterschrift und Namen erfassen.");
      return;
    }
    if (!window.confirm("Protokoll jetzt abschließen? Die Inbetriebnahme wird hiermit formal abgenommen, das PDF an Kunde und NOVATUS versendet und der Status auf 'Inbetriebnahme abgeschlossen' gesetzt. Eine spätere Änderung ist nicht mehr möglich.")) return;
    setFinalizing(true);
    try {
      const r = await axios.post(`${API_BASE}/${token}/commissioning/finalize`, {
        ...sectionPayload(),
        installer_signature: installerSig,
        customer_signature: customerSig,
      });
      if (r.data?.ok) {
        setDone(true);
        toast.success("Inbetriebnahme erfolgreich abgenommen", {
          description: "PDF wurde an Kunde, Vertriebsleiter und Buchhaltung versendet.",
          duration: 8000,
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Abschluss fehlgeschlagen");
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) {
    return <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center"><Loader2 className="animate-spin inline" /></div>;
  }
  if (!data) return null;

  if (done) {
    return (
      <section className="bg-emerald-500/10 border border-emerald-500/40 rounded-2xl p-6 text-center space-y-3">
        <FileCheck2 className="text-emerald-400 mx-auto" size={42} />
        <h2 className="font-display text-xl font-bold text-emerald-100">Inbetriebnahme abgeschlossen</h2>
        <p className="text-sm text-emerald-200/80">
          Das Inbetriebnahmeprotokoll wurde digital quittiert und als PDF an den Kunden sowie an NOVATUS versendet. Der Auftrag ist hiermit formal abgenommen.
        </p>
      </section>
    );
  }

  const P = data.protocol;

  return (
    <section data-testid="commissioning-section" className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-5">
      <h2 className="font-display text-lg font-semibold flex items-center gap-2">
        <ClipboardCheck className="text-[#00E575]" size={20} /> Inbetriebnahme-Protokoll
      </h2>
      <div className="text-[12px] text-zinc-400 bg-zinc-900/60 border border-zinc-700 rounded-xl px-3 py-2 font-medium">
        Bitte alle Felder vor Ort gemeinsam mit dem Kunden ausfüllen. Am Ende digital unterschreiben — die formale Inbetriebnahme erfolgt mit Klick auf „Protokoll abschließen".
      </div>

      {/* 1. Allgemein (read-only header from project) */}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Auftrag"><div className={inputCls + " flex items-center"}>{data.project.project_number}</div></Field>
        <Field label="Datum"><div className={inputCls + " flex items-center"}>{data.project.commissioned_at_display}</div></Field>
        <Field label="Kunde"><div className={inputCls + " flex items-center"}>{data.project.customer_name}</div></Field>
        <Field label="Adresse"><div className={inputCls + " flex items-center text-xs"}>{data.project.address}</div></Field>
      </div>

      {/* 2. Gerätedaten */}
      <div>
        <h3 className="font-display text-sm font-bold mb-2 text-[#00E575] uppercase tracking-wider">Gerätedaten</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Hersteller"><input className={inputCls} value={P.device.manufacturer || ""} onChange={(e) => update("device", "manufacturer", e.target.value)} /></Field>
          <Field label="Modell"><input className={inputCls} value={P.device.model || ""} onChange={(e) => update("device", "model", e.target.value)} /></Field>
          <Field label="Seriennummer"><input className={inputCls} value={P.device.serial_number || ""} onChange={(e) => update("device", "serial_number", e.target.value)} /></Field>
          <Field label="Leistung [kW]"><input className={inputCls} value={P.device.power_kw || ""} onChange={(e) => update("device", "power_kw", e.target.value)} /></Field>
          <Field label="Kältemittel"><input className={inputCls} value={P.device.refrigerant || ""} onChange={(e) => update("device", "refrigerant", e.target.value)} /></Field>
          <Field label="Schallleistung"><input className={inputCls} value={P.device.sound_level || ""} onChange={(e) => update("device", "sound_level", e.target.value)} /></Field>
        </div>
      </div>

      {/* 3. Hydraulik */}
      <div>
        <h3 className="font-display text-sm font-bold mb-2 text-[#00E575] uppercase tracking-wider">Hydraulik</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Anlagendruck [bar]"><input className={inputCls} value={P.hydraulics.system_pressure || ""} onChange={(e) => update("hydraulics", "system_pressure", e.target.value)} /></Field>
          <Field label="Volumenstrom [l/min]"><input className={inputCls} value={P.hydraulics.volume_flow || ""} onChange={(e) => update("hydraulics", "volume_flow", e.target.value)} /></Field>
          <Field label="Vorlauf [°C]"><input className={inputCls} value={P.hydraulics.supply_temp || ""} onChange={(e) => update("hydraulics", "supply_temp", e.target.value)} /></Field>
          <Field label="Rücklauf [°C]"><input className={inputCls} value={P.hydraulics.return_temp || ""} onChange={(e) => update("hydraulics", "return_temp", e.target.value)} /></Field>
          <Field label="Pufferspeicher"><input className={inputCls} value={P.hydraulics.buffer_tank || ""} onChange={(e) => update("hydraulics", "buffer_tank", e.target.value)} /></Field>
          <Field label="Frostschutz/Sole"><input className={inputCls} value={P.hydraulics.antifreeze || ""} onChange={(e) => update("hydraulics", "antifreeze", e.target.value)} /></Field>
        </div>
      </div>

      {/* 4. Elektrik */}
      <div>
        <h3 className="font-display text-sm font-bold mb-2 text-[#00E575] uppercase tracking-wider">Elektrik</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Netzanschluss geprüft"><Toggle3 value={P.electrical.grid_check} onChange={(v) => update("electrical", "grid_check", v)} testId="elec-grid" /></Field>
          <Field label="Absicherung [A]"><input className={inputCls} value={P.electrical.fuse_rating || ""} onChange={(e) => update("electrical", "fuse_rating", e.target.value)} /></Field>
          <Field label="FI-Schutzschalter"><Toggle3 value={P.electrical.rcd_check} onChange={(v) => update("electrical", "rcd_check", v)} testId="elec-rcd" /></Field>
          <Field label="Phasenlage geprüft"><Toggle3 value={P.electrical.phase_sequence_check} onChange={(v) => update("electrical", "phase_sequence_check", v)} testId="elec-phase" /></Field>
        </div>
      </div>

      {/* 5. Funktionsprüfung */}
      <div>
        <h3 className="font-display text-sm font-bold mb-2 text-[#00E575] uppercase tracking-wider">Funktionsprüfung</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Heizbetrieb"><StatusToggle value={P.function.heating} onChange={(v) => update("function", "heating", v)} testId="func-heat" /></Field>
          <Field label="Warmwasser"><StatusToggle value={P.function.hot_water} onChange={(v) => update("function", "hot_water", v)} testId="func-hw" /></Field>
          <Field label="Abtauvorgang"><StatusToggle value={P.function.defrost} onChange={(v) => update("function", "defrost", v)} testId="func-defrost" /></Field>
          <Field label="Regelung"><StatusToggle value={P.function.control} onChange={(v) => update("function", "control", v)} testId="func-control" /></Field>
        </div>
      </div>

      {/* 6. Messwerte */}
      <div>
        <h3 className="font-display text-sm font-bold mb-2 text-[#00E575] uppercase tracking-wider">Messwerte</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Außentemp. [°C]"><input className={inputCls} value={P.measured.ambient_temp || ""} onChange={(e) => update("measured", "ambient_temp", e.target.value)} /></Field>
          <Field label="Vorlauf [°C]"><input className={inputCls} value={P.measured.supply_temp || ""} onChange={(e) => update("measured", "supply_temp", e.target.value)} /></Field>
          <Field label="Rücklauf [°C]"><input className={inputCls} value={P.measured.return_temp || ""} onChange={(e) => update("measured", "return_temp", e.target.value)} /></Field>
          <Field label="COP / Leistung"><input className={inputCls} value={P.measured.cop_or_power || ""} onChange={(e) => update("measured", "cop_or_power", e.target.value)} /></Field>
        </div>
      </div>

      {/* 7. Übergabe */}
      <div>
        <h3 className="font-display text-sm font-bold mb-2 text-[#00E575] uppercase tracking-wider">Übergabe an den Kunden</h3>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Field label="Einweisung erfolgt"><Toggle3 value={P.handover.instruction_done} onChange={(v) => update("handover", "instruction_done", v)} testId="handover-instr" /></Field>
          <Field label="Doku übergeben"><Toggle3 value={P.handover.documentation_handed} onChange={(v) => update("handover", "documentation_handed", v)} testId="handover-doc" /></Field>
        </div>
        <Field label="Festgestellte Mängel">
          <textarea className={inputCls + " h-20 py-2"} value={P.handover.defects || ""} onChange={(e) => update("handover", "defects", e.target.value)} placeholder="z. B. keine, oder fehlende Bauteile…" />
        </Field>
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10.5px] font-bold uppercase tracking-wider text-zinc-400">Notizen / Anmerkungen</span>
            <NotesDictation token={token} onTranscript={(t) => update("handover", "notes", ((P.handover.notes || "").trim() + " " + t).trim())} />
          </div>
          <textarea className={inputCls + " h-24 py-2"} value={P.handover.notes || ""} onChange={(e) => update("handover", "notes", e.target.value)} placeholder="Diktiere oder schreibe ergänzende Anmerkungen…" data-testid="commissioning-notes-textarea" />
        </div>
      </div>

      {/* Save draft */}
      <button onClick={save} disabled={saving} data-testid="commissioning-save-btn" className="h-11 w-full rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={16} /> Zwischenstand speichern</>}
      </button>

      {/* Signatures */}
      <div className="pt-2 border-t border-zinc-800">
        <h3 className="font-display text-sm font-bold mb-3 text-[#00E575] uppercase tracking-wider">Unterschriften</h3>
        <div className="space-y-4">
          <div>
            <Field label="Name Installation"><input className={inputCls} value={installerSig.name} onChange={(e) => setInstallerSig({ ...installerSig, name: e.target.value })} placeholder="Vor- &amp; Nachname" data-testid="sig-installer-name" /></Field>
            <div className="mt-2">
              <SignaturePad label="Unterschrift Installation" value={installerSig.data_url} onChange={(d) => setInstallerSig((s) => ({ ...s, data_url: d }))} testId="sig-installer-pad" />
            </div>
          </div>
          <div>
            <Field label="Name Kunde"><input className={inputCls} value={customerSig.name} onChange={(e) => setCustomerSig({ ...customerSig, name: e.target.value })} placeholder={data.project.customer_name} data-testid="sig-customer-name" /></Field>
            <div className="mt-2">
              <SignaturePad label="Unterschrift Kunde" value={customerSig.data_url} onChange={(d) => setCustomerSig((s) => ({ ...s, data_url: d }))} testId="sig-customer-pad" />
            </div>
          </div>
        </div>
      </div>

      <button onClick={finalize} disabled={finalizing} data-testid="commissioning-finalize-btn" className="h-12 w-full rounded-xl bg-[#00E575] text-[#09090B] font-bold text-sm hover:bg-[#00C964] active:scale-95 transition disabled:opacity-50 inline-flex items-center justify-center gap-2">
        {finalizing ? <Loader2 size={16} className="animate-spin" /> : <><FileCheck2 size={16} /> Protokoll abschließen &amp; Inbetriebnahme abnehmen</>}
      </button>
    </section>
  );
};
